import { useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useMutation } from '@apollo/client/react';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { authErrorMessage, type AuthSession } from '~utils/auth';
import { styles } from './styles';
import { LOGIN, LOGIN_WITH_CODE, SEND_LOGIN_CODE } from './operations';
type Props = {
  onLogin: (session: AuthSession) => void;
  onError: (message: string | null) => void;
};
type Mode = 'password' | 'code';

// Credential sign-in: password (email / username / phone) or a one-time code,
// switched by app-style tabs. OAuth lives in the ContinueWith* components.
export function Login({
  onLogin,
  onError
}: Props) {
  const [mode, setMode] = useState<Mode>('password');
  return <div {...stylex.props(styles.login)}>
      <div {...stylex.props(styles.modeRow)} role="tablist">
        <button type="button" role="tab" aria-selected={mode === 'password'} {...stylex.props(styles.modeTab, mode === 'password' && styles.modeTabActive)} onClick={() => selectMode({
        setMode,
        onError
      }, 'password')}>
          Password
        </button>
        <button type="button" role="tab" aria-selected={mode === 'code'} {...stylex.props(styles.modeTab, mode === 'code' && styles.modeTabActive)} onClick={() => selectMode({
        setMode,
        onError
      }, 'code')}>
          One-time code
        </button>
      </div>
      {mode === 'password' ? <PasswordForm onLogin={onLogin} onError={onError} /> : <CodeForm onLogin={onLogin} onError={onError} />}
    </div>;
}
function PasswordForm({
  onLogin,
  onError
}: Props) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [login, {
    loading
  }] = useMutation(LOGIN);
  const deps: PasswordDeps = {
    identifier,
    password,
    login,
    onLogin,
    onError
  };
  return <form {...stylex.props(styles.form)} onSubmit={e => submitPassword(deps, e)}>
      <label {...stylex.props(styles.label)}>
        Email, username or phone
        <input {...stylex.props(styles.input)} value={identifier} onChange={e => setIdentifier(e.target.value)} autoComplete="username" required />
      </label>
      <label {...stylex.props(styles.label)}>
        Password
        <input {...stylex.props(styles.input)} type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
      </label>
      <button type="submit" {...stylex.props(styles.submit)} disabled={loading}>
        {loading ? 'Signing in…' : 'Sign in'}
      </button>
    </form>;
}
function CodeForm({
  onLogin,
  onError
}: Props) {
  const [contact, setContact] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState<Date | null>(null);
  const [sendLoginCode, {
    loading: sending
  }] = useMutation(SEND_LOGIN_CODE);
  const [loginWithCode, {
    loading: verifying
  }] = useMutation(LOGIN_WITH_CODE);
  if (!sentTo) {
    const deps: SendCodeDeps = {
      contact,
      sendLoginCode,
      setSentTo,
      setResendAt,
      setCode,
      onError
    };
    return <form {...stylex.props(styles.form)} onSubmit={e => sendCode(deps, e)}>
        <label {...stylex.props(styles.label)}>
          Email or phone (with country code, e.g. +1…)
          <input {...stylex.props(styles.input)} value={contact} onChange={e => setContact(e.target.value)} autoComplete="username" required />
        </label>
        <button type="submit" {...stylex.props(styles.submit)} disabled={sending}>
          {sending ? 'Sending…' : 'Send code'}
        </button>
      </form>;
  }
  const deps: VerifyCodeDeps = {
    contact,
    code,
    loginWithCode,
    onLogin,
    onError
  };
  return <form {...stylex.props(styles.form)} onSubmit={e => verifyCode(deps, e)}>
      <p {...stylex.props(styles.hint)}>
        We sent a 6-digit code to {sentTo}. It expires in 2 minutes.
      </p>
      <label {...stylex.props(styles.label)}>
        Code
        <input {...stylex.props(styles.input)} inputMode="numeric" pattern="[0-9]{6}" value={code} onChange={e => setCode(e.target.value)} autoComplete="one-time-code" required />
      </label>
      <button type="submit" {...stylex.props(styles.submit)} disabled={verifying}>
        {verifying ? 'Verifying…' : 'Sign in'}
      </button>
      <button type="button" {...stylex.props(styles.linkButton)} onClick={() => resetContact({
      resendAt,
      setSentTo
    })}>
        Use a different email or phone
      </button>
    </form>;
}
const selectMode = ({
  setMode,
  onError
}: {
  setMode: (mode: Mode) => void;
  onError: Props['onError'];
}, next: Mode) => {
  setMode(next);
  onError(null);
};
type LoginFn = (options: {
  variables: VariablesOf<typeof LOGIN>;
}) => Promise<{
  data?: ResultOf<typeof LOGIN> | null;
}>;
type PasswordDeps = Props & {
  identifier: string;
  password: string;
  login: LoginFn;
};
const submitPassword = async ({
  identifier,
  password,
  login,
  onLogin,
  onError
}: PasswordDeps, event: React.FormEvent) => {
  event.preventDefault();
  onError(null);
  try {
    const result = await login({
      variables: {
        input: {
          identifier: identifier.trim(),
          password
        }
      }
    });
    if (result.data) onLogin(result.data.login);
  } catch (err: unknown) {
    onError(authErrorMessage(err));
  }
};
const toContactInfo = (contact: string) => {
  const value = contact.trim();
  return {
    type: value.includes('@') ? 'EMAIL' as const : 'PHONE' as const,
    value
  };
};
type SendLoginCodeFn = (options: {
  variables: VariablesOf<typeof SEND_LOGIN_CODE>;
}) => Promise<{
  data?: ResultOf<typeof SEND_LOGIN_CODE> | null;
}>;
type SendCodeDeps = {
  contact: string;
  sendLoginCode: SendLoginCodeFn;
  setSentTo: (value: string | null) => void;
  setResendAt: (value: Date | null) => void;
  setCode: (value: string) => void;
  onError: Props['onError'];
};
const sendCode = async ({
  contact,
  sendLoginCode,
  setSentTo,
  setResendAt,
  setCode,
  onError
}: SendCodeDeps, event: React.FormEvent) => {
  event.preventDefault();
  onError(null);
  try {
    const result = await sendLoginCode({
      variables: {
        contactInfo: toContactInfo(contact)
      }
    });
    if (result.data) {
      setSentTo(contact.trim());
      setResendAt(new Date(result.data.sendLoginCode));
      setCode('');
    }
  } catch (err: unknown) {
    onError(authErrorMessage(err));
  }
};
type LoginWithCodeFn = (options: {
  variables: VariablesOf<typeof LOGIN_WITH_CODE>;
}) => Promise<{
  data?: ResultOf<typeof LOGIN_WITH_CODE> | null;
}>;
type VerifyCodeDeps = Props & {
  contact: string;
  code: string;
  loginWithCode: LoginWithCodeFn;
};
const verifyCode = async ({
  contact,
  code,
  loginWithCode,
  onLogin,
  onError
}: VerifyCodeDeps, event: React.FormEvent) => {
  event.preventDefault();
  onError(null);
  try {
    const result = await loginWithCode({
      variables: {
        input: {
          contactInfo: toContactInfo(contact),
          code: code.trim()
        }
      }
    });
    if (result.data) onLogin(result.data.loginWithCode);
  } catch (err: unknown) {
    onError(authErrorMessage(err));
  }
};
const resetContact = ({
  resendAt,
  setSentTo
}: {
  resendAt: Date | null;
  setSentTo: (value: string | null) => void;
}) => {
  if (resendAt && resendAt.getTime() > Date.now()) return;
  setSentTo(null);
};